import React from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { AskEvansSession } from '../../api/askEvansApi'

function formatWhen(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

export function AskEvansSessionBar({
  sessions,
  activeSessionId,
  onSelect,
  onNewChat,
  onDelete
}: {
  sessions: AskEvansSession[]
  activeSessionId?: string | null
  onSelect: (id: string) => void
  onNewChat: () => void
  onDelete: (id: string) => void
}) {
  return (
    <aside className="flex h-full w-[260px] shrink-0 flex-col border-r border-[#E2E5EA] bg-white" aria-label="Ask Evans chat history">
      <div className="flex h-[60px] shrink-0 items-center border-b border-[#E2E5EA] px-4">
        <div>
          <div className="font-heading text-[14px] font-semibold text-blueprint-blue">Chats</div>
          <div className="text-[11px] text-heritage-grey">Your Ask Evans sessions</div>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-3 py-3">
        <button
          type="button"
          onClick={onNewChat}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg border border-[#E2E5EA] bg-page-bg px-3 py-2 text-[12px] font-medium text-blueprint-blue transition hover:border-hero-red/30 hover:bg-hero-red/5"
        >
          <PlusIcon className="h-4 w-4" strokeWidth={2} aria-hidden />
          New Chat Session
        </button>
        <p className="shrink-0 px-1 pb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-heritage-grey">Chat History</p>
        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          {sessions.length === 0 ? (
            <div className="px-2 pt-4 text-center text-[11px] text-heritage-grey">No previous sessions.</div>
          ) : (
            sessions.map((session) => {
              const active = session.id === activeSessionId
              return (
                <div
                  key={session.id}
                  className={`group flex items-center justify-between rounded-lg px-2 py-1.5 ${
                    active ? 'bg-hero-red/10 text-blueprint-blue' : 'text-text-grey hover:bg-page-bg'
                  }`}
                >
                  <button type="button" onClick={() => onSelect(session.id)} className="min-w-0 flex-1 truncate pr-2 text-left text-[12px]">
                    <div className={`truncate ${active ? 'font-medium' : ''}`}>{session.title || 'Untitled chat'}</div>
                    <div className="font-heading text-[9px] text-heritage-grey">{formatWhen(session.lastActiveAt)}</div>
                  </button>
                  <button
                    type="button"
                    aria-label="Delete session"
                    className="shrink-0 rounded p-1 text-heritage-grey opacity-0 transition hover:text-hero-red group-hover:opacity-100 focus:opacity-100"
                    onClick={() => onDelete(session.id)}
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )
            })
          )}
        </div>
      </div>
    </aside>
  )
}
